import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Target, ArrowRight } from 'lucide-react';
import { clsx } from 'clsx';
import {
  creativeWorkbenchApi,
  type WorkbenchSteeringResponse,
  type WorkbenchSteeringCell,
} from '@/services/api';
import { useCreativeWorkbench } from '../CreativeWorkbenchContext';
import type { ChatSeedInput } from './EmbeddedChatPanel';

interface Props {
  onCellSelect?: (cell: WorkbenchSteeringCell) => void;
  onDrillDown: (seed: ChatSeedInput) => void;
  compact?: boolean;
}

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

export function PersonaSteeringMatrix({ onCellSelect, onDrillDown, compact }: Props) {
  const navigate = useNavigate();
  const { clientUid } = useCreativeWorkbench();
  const [data, setData] = useState<WorkbenchSteeringResponse | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!clientUid) return;
    let cancelled = false;
    setLoading(true);
    creativeWorkbenchApi
      .getSteeringMatrix({ clientUid })
      .then((p) => {
        if (!cancelled) setData(p);
      })
      .catch(() => {
        if (!cancelled) setData(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [clientUid]);

  // Build a lookup: target -> actual -> cell
  const grid = useMemo(() => {
    const m = new Map<string, Map<string, WorkbenchSteeringCell>>();
    for (const c of data?.cells ?? []) {
      const row = m.get(c.target_persona) ?? new Map();
      row.set(c.actual_persona, c);
      m.set(c.target_persona, row);
    }
    return m;
  }, [data]);

  const personas = data?.personas ?? [];
  const accuracy = data?.summary.steering_accuracy_pct ?? 0;

  return (
    <section className="bg-white border border-slate-200 rounded-xl p-4 space-y-3">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-bold text-navy-900 flex items-center gap-1.5">
            <Target size={14} className="text-teal-500" />
            Persona Steering Matrix
          </h2>
          <p className="text-[10px] text-slate-400 mt-0.5">
            Are we delivering creative to the persona we targeted? Diagonal =
            on-target. <span className="font-bold text-teal-600">{accuracy.toFixed(1)}%</span>{' '}
            steering accuracy.
          </p>
        </div>
        {!compact && (
          <button
            onClick={() => navigate('/creative/performance/steering')}
            className="text-[10px] font-mono text-teal-600 hover:text-teal-700 inline-flex items-center gap-1"
          >
            Open investigation <ArrowRight size={10} />
          </button>
        )}
      </header>

      {loading && !data && (
        <div className="text-xs text-slate-400">Loading matrix...</div>
      )}

      {data && personas.length > 0 && (
        <div className="overflow-x-auto">
          <table className="text-[10px] font-mono w-full border-collapse">
            <thead>
              <tr>
                <th className="text-left text-slate-400 px-1.5 py-1 sticky left-0 bg-white">
                  target ↓ / actual →
                </th>
                {personas.map((p) => (
                  <th
                    key={p}
                    className="text-left text-slate-500 px-1.5 py-1 truncate max-w-[120px]"
                    title={p}
                  >
                    {p}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {personas.map((target) => {
                const row = grid.get(target) ?? new Map();
                return (
                  <tr key={target} className="border-t border-slate-100">
                    <td
                      className="text-slate-700 font-bold px-1.5 py-1 sticky left-0 bg-white truncate max-w-[140px]"
                      title={target}
                    >
                      {target}
                    </td>
                    {personas.map((actual) => {
                      const cell = row.get(actual);
                      const intensity = cell ? Math.min(1, cell.row_share_pct / 100) : 0;
                      const onDiagonal = target === actual && target !== 'Uncategorized';
                      return (
                        <td
                          key={actual}
                          className="px-0 py-0 text-center border border-slate-50"
                        >
                          {cell ? (
                            <button
                              onClick={() => {
                                onCellSelect?.(cell);
                                onDrillDown({
                                  question: `Why are ${actual} customers clicking on ads we targeted at ${target}?`,
                                  chart_axis: {
                                    series: 'steering',
                                    dimension: 'matrix',
                                    target_persona: target,
                                    actual_persona: actual,
                                  },
                                });
                              }}
                              className={clsx(
                                'block w-full h-full px-1.5 py-2 transition-colors',
                                onDiagonal
                                  ? 'hover:bg-emerald-100'
                                  : 'hover:bg-amber-100',
                              )}
                              style={{
                                backgroundColor: onDiagonal
                                  ? `rgba(16, 185, 129, ${0.05 + intensity * 0.5})`
                                  : `rgba(245, 158, 11, ${0.03 + intensity * 0.3})`,
                              }}
                              title={`${cell.ad_count} ads · ${cell.row_share_pct}% of target spend`}
                            >
                              <div className="text-navy-900 font-bold">
                                {CURRENCY.format(cell.spend)}
                              </div>
                              <div className="text-[9px] text-slate-500">
                                {cell.row_share_pct.toFixed(0)}% · {cell.ad_count}
                              </div>
                            </button>
                          ) : (
                            <span className="text-slate-200 text-xs">·</span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {data && data.cells.length === 0 && !loading && (
        <div className="text-xs text-slate-400">No steering data yet.</div>
      )}
    </section>
  );
}
